import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import DOMPurify from "dompurify";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { User, Lock, Bell, Home, Shield, Mail, Users as UsersIcon, Download, LogOut, Trash2, Plus, ChevronRight } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Sanitize once via DOMPurify (useMemo) and mount as inert HTML.
 * Extracted so the sanitized string is memoized per-html and to avoid
 * an inline object literal being re-created on every render.
 */
function SanitizedHtml({ html }) {
  const clean = useMemo(
    () => DOMPurify.sanitize(html || "", { USE_PROFILES: { html: true } }),
    [html],
  );
  // eslint-disable-next-line react/no-danger
  return <div className="mt-3 border border-[#D5CFC4] rounded-xl p-4 bg-white" dangerouslySetInnerHTML={{ __html: clean }} />;
}

export default function MyAccountPage() {
  const { user, refresh } = useAuth();
  const [profile, setProfile] = useState(null);
  const [tab, setTab] = useState("profile");

  const load = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setProfile({
        name: data.name || "",
        display_name: data.display_name || "",
        email: data.email || "",
        phone: data.phone || "",
        contact_method: data.contact_method || "email",
        hide_name_public: !!data.hide_name_public,
        emergency_contact_name: data.emergency_contact_name || "",
        emergency_contact_phone: data.emergency_contact_phone || "",
        avatar_url: data.avatar_url || "",
        admin_reply_to: data.admin_reply_to || "",
        admin_signature: data.admin_signature || "",
        notify_prefs: {
          booking_updates: data.notify_prefs?.booking_updates ?? true,
          dues_reminders: data.notify_prefs?.dues_reminders ?? true,
          announcements: data.notify_prefs?.announcements ?? true,
          deposits: data.notify_prefs?.deposits ?? true,
        },
        household_members: data.household_members || [],
        mailing_address_line1: data.mailing_address_line1 || "",
        mailing_address_line2: data.mailing_address_line2 || "",
        mailing_city: data.mailing_city || "",
        mailing_state: data.mailing_state || "",
        mailing_zip: data.mailing_zip || "",
        mailing_same_as_property: data.mailing_same_as_property !== false,
        role: data.role,
      });
    } catch { toast.error("Failed to load profile"); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps -- intentional load-once on mount

  if (!profile) return <div className="max-w-4xl mx-auto p-8 text-[#5C6A64]">Loading…</div>;

  const save = async (fields) => {
    try {
      const { data } = await api.patch("/users/me", fields);
      toast.success("Saved");
      setProfile((p) => ({ ...p, ...fields }));
      if (fields.email || fields.name) refresh?.();
      return data;
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="my-account-page">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-14 h-14 rounded-full bg-[#EADCC8] flex items-center justify-center overflow-hidden">
          {profile.avatar_url
            ? <img src={profile.avatar_url} alt="avatar" className="w-full h-full object-cover" />
            : <User className="w-7 h-7 text-[#2C4C3B]" />}
        </div>
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#C86650]">Account</p>
          <h1 className="font-serif text-3xl text-[#1A2421]">{profile.display_name || profile.name}</h1>
          <p className="text-sm text-[#5C6A64]">{profile.email}</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#EADCC8] rounded-full p-1 flex flex-wrap h-auto">
          <TabsTrigger data-testid="acct-tab-profile" value="profile"><User className="w-4 h-4 mr-2" />Profile</TabsTrigger>
          <TabsTrigger data-testid="acct-tab-notifs" value="notifs"><Bell className="w-4 h-4 mr-2" />Notifications</TabsTrigger>
          <TabsTrigger data-testid="acct-tab-household" value="household"><UsersIcon className="w-4 h-4 mr-2" />Household</TabsTrigger>
          <TabsTrigger data-testid="acct-tab-privacy" value="privacy"><Shield className="w-4 h-4 mr-2" />Privacy</TabsTrigger>
          <TabsTrigger data-testid="acct-tab-security" value="security"><Lock className="w-4 h-4 mr-2" />Security</TabsTrigger>
          <TabsTrigger data-testid="acct-tab-comms" value="comms"><Mail className="w-4 h-4 mr-2" />Emails &amp; Statements</TabsTrigger>
          <TabsTrigger data-testid="acct-tab-property" value="property"><Home className="w-4 h-4 mr-2" />My Property</TabsTrigger>
          {profile.role === "admin" && <TabsTrigger data-testid="acct-tab-admin" value="admin"><Shield className="w-4 h-4 mr-2" />Admin</TabsTrigger>}
        </TabsList>

        <TabsContent value="profile" className="mt-6">
          <ProfileTab profile={profile} setProfile={setProfile} save={save} />
        </TabsContent>
        <TabsContent value="notifs" className="mt-6">
          <NotifsTab profile={profile} setProfile={setProfile} save={save} />
        </TabsContent>
        <TabsContent value="household" className="mt-6">
          <HouseholdTab profile={profile} setProfile={setProfile} save={save} />
        </TabsContent>
        <TabsContent value="privacy" className="mt-6">
          <PrivacyTab profile={profile} setProfile={setProfile} save={save} />
        </TabsContent>
        <TabsContent value="security" className="mt-6">
          <SecurityTab />
        </TabsContent>
        <TabsContent value="comms" className="mt-6">
          <CommsTab />
        </TabsContent>
        <TabsContent value="property" className="mt-6">
          <PropertyTab />
        </TabsContent>
        {profile.role === "admin" && (
          <TabsContent value="admin" className="mt-6">
            <AdminTab profile={profile} setProfile={setProfile} save={save} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function ProfileTab({ profile, setProfile, save }) {
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) return toast.error("Max 2 MB");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", `avatar-${Date.now()}`);
    fd.append("category", "General");
    try {
      const { data } = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${BACKEND}/api/documents/download/${data.storage_path}`;
      await save({ avatar_url: url });
      setProfile((p) => ({ ...p, avatar_url: url }));
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
  };
  return (
    <Card className="rounded-2xl border-[#D5CFC4]">
      <CardHeader><CardTitle className="font-serif text-2xl">Profile</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-[#EADCC8] flex items-center justify-center overflow-hidden">
            {profile.avatar_url
              ? <img src={profile.avatar_url} alt="avatar" className="w-full h-full object-cover" />
              : <User className="w-8 h-8 text-[#2C4C3B]" />}
          </div>
          <label className="text-sm font-medium text-[#2C4C3B] cursor-pointer">
            <input data-testid="avatar-file" type="file" accept="image/*" onChange={upload} className="hidden" />
            <span className="underline">Change avatar</span>
            <span className="text-xs text-[#5C6A64] ml-2">Max 2 MB · JPG or PNG</span>
          </label>
        </div>
        <FieldRow label="Full name" value={profile.name} onChange={(v) => setProfile({ ...profile, name: v })} testid="profile-name" />
        <FieldRow label="Display name" value={profile.display_name} onChange={(v) => setProfile({ ...profile, display_name: v })} testid="profile-display" help="Shown in navbar and on your submissions." />
        <FieldRow label="Email address" value={profile.email} onChange={(v) => setProfile({ ...profile, email: v })} testid="profile-email" type="email" />
        <FieldRow label="Phone" value={profile.phone} onChange={(v) => setProfile({ ...profile, phone: v })} testid="profile-phone" />
        <div className="border-t border-[#D5CFC4] my-3" />
        <p className="text-xs uppercase tracking-wider text-[#5C6A64]">Emergency contact</p>
        <FieldRow label="Contact name" value={profile.emergency_contact_name} onChange={(v) => setProfile({ ...profile, emergency_contact_name: v })} testid="emergency-name" />
        <FieldRow label="Contact phone" value={profile.emergency_contact_phone} onChange={(v) => setProfile({ ...profile, emergency_contact_phone: v })} testid="emergency-phone" />
        <div className="border-t border-[#D5CFC4] my-3" />
        <p className="text-xs uppercase tracking-wider text-[#5C6A64]">Mailing address</p>
        <label className="flex items-center gap-2 text-sm text-[#1A2421] cursor-pointer" data-testid="mailing-same-toggle">
          <input
            type="checkbox"
            checked={!!profile.mailing_same_as_property}
            onChange={(e) => setProfile({ ...profile, mailing_same_as_property: e.target.checked })}
            data-testid="mailing-same-checkbox"
          />
          Same as my property address
        </label>
        {!profile.mailing_same_as_property && (
          <div className="space-y-3">
            <FieldRow label="Address line 1" value={profile.mailing_address_line1} onChange={(v) => setProfile({ ...profile, mailing_address_line1: v })} testid="mailing-line1" />
            <FieldRow label="Address line 2 (optional)" value={profile.mailing_address_line2} onChange={(v) => setProfile({ ...profile, mailing_address_line2: v })} testid="mailing-line2" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <FieldRow label="City" value={profile.mailing_city} onChange={(v) => setProfile({ ...profile, mailing_city: v })} testid="mailing-city" />
              <FieldRow label="State" value={profile.mailing_state} onChange={(v) => setProfile({ ...profile, mailing_state: v })} testid="mailing-state" />
              <FieldRow label="ZIP" value={profile.mailing_zip} onChange={(v) => setProfile({ ...profile, mailing_zip: v })} testid="mailing-zip" />
            </div>
            <p className="text-xs text-[#5C6A64]">Used for any HOA mail that must be sent somewhere other than your property (e.g. rental owners, seasonal residents).</p>
          </div>
        )}
        <Button data-testid="profile-save" onClick={() => save({
          name: profile.name, display_name: profile.display_name, email: profile.email,
          phone: profile.phone, emergency_contact_name: profile.emergency_contact_name,
          emergency_contact_phone: profile.emergency_contact_phone,
          mailing_same_as_property: !!profile.mailing_same_as_property,
          mailing_address_line1: profile.mailing_address_line1,
          mailing_address_line2: profile.mailing_address_line2,
          mailing_city: profile.mailing_city,
          mailing_state: profile.mailing_state,
          mailing_zip: profile.mailing_zip,
        })} className="rounded-full bg-[#2C4C3B]">Save changes</Button>
      </CardContent>
    </Card>
  );
}

function FieldRow({ label, value, onChange, testid, type = "text", help }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-[#5C6A64]">{label}</label>
      <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 rounded-xl" data-testid={testid} />
      {help && <p className="text-xs text-[#5C6A64] mt-1">{help}</p>}
    </div>
  );
}

function NotifsTab({ profile, setProfile, save }) {
  const toggle = (k) => setProfile({ ...profile, notify_prefs: { ...profile.notify_prefs, [k]: !profile.notify_prefs[k] } });
  const items = [
    ["booking_updates", "Amenity booking status updates"],
    ["dues_reminders", "Dues & delinquency reminders"],
    ["announcements", "New community announcements"],
    ["deposits", "Deposit charged / refunded receipts"],
  ];
  return (
    <Card className="rounded-2xl border-[#D5CFC4]">
      <CardHeader><CardTitle className="font-serif text-2xl">Notifications</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Preferred contact channel</label>
          <Select value={profile.contact_method} onValueChange={(v) => setProfile({ ...profile, contact_method: v })}>
            <SelectTrigger className="mt-1 rounded-xl w-56" data-testid="contact-method"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="email">Email</SelectItem>
              <SelectItem value="sms">Text message (SMS)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-[#5C6A64] mt-1">SMS delivery requires a valid phone number on file.</p>
        </div>
        <div className="border-t border-[#D5CFC4] my-3" />
        <div className="space-y-2">
          {items.map(([k, l]) => (
            <label key={k} className="flex items-center gap-3 p-3 bg-[#F9F8F6] rounded-xl cursor-pointer">
              <input data-testid={`notif-${k}`} type="checkbox" checked={!!profile.notify_prefs[k]} onChange={() => toggle(k)} />
              <span className="text-sm text-[#1A2421]">{l}</span>
            </label>
          ))}
        </div>
        <Button data-testid="notifs-save" onClick={() => save({ contact_method: profile.contact_method, notify_prefs: profile.notify_prefs })} className="rounded-full bg-[#2C4C3B]">Save preferences</Button>
      </CardContent>
    </Card>
  );
}

function HouseholdTab({ profile, setProfile, save }) {
  const add = () => setProfile({ ...profile, household_members: [...(profile.household_members || []), { name: "", relationship: "spouse", phone: "" }] });
  const remove = (i) => setProfile({ ...profile, household_members: profile.household_members.filter((_, idx) => idx !== i) });
  const upd = (i, k, v) => {
    const list = [...profile.household_members];
    list[i] = { ...list[i], [k]: v };
    setProfile({ ...profile, household_members: list });
  };
  return (
    <Card className="rounded-2xl border-[#D5CFC4]">
      <CardHeader><CardTitle className="font-serif text-2xl">Household members</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-[#5C6A64]">Add other adults or authorized users at your address. They can be listed on amenity bookings and reached in emergencies. Only one login account per household.</p>
        {(profile.household_members || []).length === 0 && <p className="text-sm text-[#5C6A64] italic">No household members added yet.</p>}
        {(profile.household_members || []).map((m, i) => (
          <div key={m._uid || `hm-${i}-${m.name || ""}`} className="grid grid-cols-1 sm:grid-cols-[1fr,150px,1fr,40px] gap-2 items-end" data-testid={`household-row-${i}`}>
            <Input placeholder="Name" value={m.name} onChange={(e) => upd(i, "name", e.target.value)} data-testid={`hm-name-${i}`} />
            <Select value={m.relationship} onValueChange={(v) => upd(i, "relationship", v)}>
              <SelectTrigger data-testid={`hm-rel-${i}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {["spouse", "partner", "adult child", "parent", "roommate", "other"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input placeholder="Phone (optional)" value={m.phone || ""} onChange={(e) => upd(i, "phone", e.target.value)} data-testid={`hm-phone-${i}`} />
            <Button size="icon" variant="ghost" onClick={() => remove(i)} data-testid={`hm-remove-${i}`}><Trash2 className="w-4 h-4 text-[#C86650]" /></Button>
          </div>
        ))}
        <Button variant="outline" onClick={add} className="rounded-full" data-testid="hm-add"><Plus className="w-4 h-4 mr-1" />Add member</Button>
        <div>
          <Button onClick={() => save({ household_members: profile.household_members })} className="rounded-full bg-[#2C4C3B]" data-testid="hm-save">Save household</Button>
        </div>
      </CardContent>
    </Card>
  );
}

function PrivacyTab({ profile, setProfile, save }) {
  return (
    <Card className="rounded-2xl border-[#D5CFC4]">
      <CardHeader><CardTitle className="font-serif text-2xl">Privacy</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <label className="flex items-start gap-3 p-4 bg-[#F9F8F6] rounded-xl cursor-pointer">
          <input data-testid="hide-name" type="checkbox" checked={profile.hide_name_public} onChange={(e) => setProfile({ ...profile, hide_name_public: e.target.checked })} className="mt-1" />
          <div>
            <p className="text-sm font-medium text-[#1A2421]">Hide my name from public views</p>
            <p className="text-xs text-[#5C6A64] mt-1">Your name will show as "A homeowner" on publicly visible submitted announcements. Board members still see your full name.</p>
          </div>
        </label>
        <Button data-testid="privacy-save" onClick={() => save({ hide_name_public: profile.hide_name_public })} className="rounded-full bg-[#2C4C3B]">Save</Button>
      </CardContent>
    </Card>
  );
}

function SecurityTab() {
  const [pw, setPw] = useState({ old_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (pw.new_password !== pw.confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/users/me/password", { old_password: pw.old_password, new_password: pw.new_password });
      toast.success("Password changed");
      setPw({ old_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const revoke = async () => {
    if (!window.confirm("Sign out from all devices? You'll need to sign in again.")) return;
    try {
      await api.post("/users/me/sign-out-everywhere");
      toast.success("Signed out everywhere");
      setTimeout(() => (window.location.href = "/login"), 800);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-4">
      <Card className="rounded-2xl border-[#D5CFC4]">
        <CardHeader><CardTitle className="font-serif text-2xl">Change password</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Input data-testid="pw-old" type="password" placeholder="Current password" value={pw.old_password} onChange={(e) => setPw({ ...pw, old_password: e.target.value })} />
          <Input data-testid="pw-new" type="password" placeholder="New password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} />
          <Input data-testid="pw-confirm" type="password" placeholder="Confirm new password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} />
          <Button data-testid="pw-submit" onClick={submit} disabled={busy} className="rounded-full bg-[#2C4C3B]">{busy ? "Saving…" : "Change password"}</Button>
        </CardContent>
      </Card>
      <Card className="rounded-2xl border-[#D5CFC4]">
        <CardHeader><CardTitle className="font-serif text-2xl">Sessions</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-[#5C6A64]">If you've signed in on a shared device, revoke access everywhere. You'll need to sign in again.</p>
          <Button data-testid="signout-everywhere" variant="outline" onClick={revoke} className="rounded-full border-[#C86650] text-[#C86650]"><LogOut className="w-4 h-4 mr-2" />Sign out everywhere</Button>
        </CardContent>
      </Card>
      <DangerCard />
    </div>
  );
}

function DangerCard() {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const doDelete = async () => {
    try {
      await api.delete("/users/me");
      toast.success("Account deleted");
      setTimeout(() => (window.location.href = "/"), 800);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <>
      <Card className="rounded-2xl border-[#C86650] bg-[#FDF3F0]">
        <CardHeader><CardTitle className="font-serif text-2xl text-[#C86650]">Danger zone</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-[#1A2421] mb-3">Delete your account permanently. Board access to your payment history for auditing purposes will be retained.</p>
          <Button data-testid="delete-account-btn" variant="outline" className="rounded-full border-[#C86650] text-[#C86650]" onClick={() => setOpen(true)}>
            <Trash2 className="w-4 h-4 mr-2" />Delete my account
          </Button>
        </CardContent>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-[#C86650]">Delete account?</DialogTitle></DialogHeader>
          <p className="text-sm">Type <b>DELETE</b> to confirm.</p>
          <Input data-testid="delete-confirm-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} />
          <Button data-testid="delete-confirm-btn" onClick={doDelete} disabled={confirmText !== "DELETE"} className="rounded-full bg-[#C86650]">Delete permanently</Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CommsTab() {
  const [emails, setEmails] = useState([]);
  const [selected, setSelected] = useState(null);
  useEffect(() => {
    // Mount-only fetch. `api` is a stable module import; `setEmails` is a stable setter.
    api.get("/users/me/emails").then(({ data }) => setEmails(data)).catch(() => setEmails([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const dl = () => {
    const yr = new Date().getFullYear();
    window.open(`${BACKEND}/api/users/me/statement?year=${yr}`, "_blank");
  };
  return (
    <div className="space-y-4">
      <Card className="rounded-2xl border-[#D5CFC4]">
        <CardHeader><CardTitle className="font-serif text-2xl">Year-end statement</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-[#5C6A64] mb-3">Download a CSV of every HOA payment you've made this calendar year — perfect for tax filings.</p>
          <Button data-testid="statement-download" onClick={dl} className="rounded-full bg-[#2C4C3B]"><Download className="w-4 h-4 mr-2" />Download {new Date().getFullYear()} statement</Button>
        </CardContent>
      </Card>
      <Card className="rounded-2xl border-[#D5CFC4]">
        <CardHeader><CardTitle className="font-serif text-2xl">Communication log</CardTitle></CardHeader>
        <CardContent>
          {emails.length === 0 ? <p className="text-sm text-[#5C6A64]">No emails sent to your address yet.</p> : (
            <div className="space-y-1">
              {emails.map((e) => (
                <button key={e.id} onClick={() => setSelected(e)} className="w-full text-left p-3 rounded-xl hover:bg-[#F9F8F6] border border-transparent hover:border-[#D5CFC4] flex items-center justify-between" data-testid={`email-log-${e.id}`}>
                  <div>
                    <p className="text-sm font-medium text-[#1A2421]">{e.subject}</p>
                    <p className="text-xs text-[#5C6A64]">{new Date(e.created_at).toLocaleString()}</p>
                  </div>
                  <ChevronRight className="w-4 h-4 text-[#5C6A64]" />
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{selected?.subject}</DialogTitle></DialogHeader>
          <p className="text-xs text-[#5C6A64]">{selected && new Date(selected.created_at).toLocaleString()}</p>
          <SanitizedHtml html={selected?.html} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PropertyTab() {
  const [prop, setProp] = useState(null);
  useEffect(() => {
    // Mount-only fetch. `api` is a stable module import; `setProp` is a stable setter.
    api.get("/properties/my").then(({ data }) => setProp(data)).catch(() => setProp(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Card className="rounded-2xl border-[#D5CFC4]">
      <CardHeader><CardTitle className="font-serif text-2xl">My property</CardTitle></CardHeader>
      <CardContent>
        {!prop ? (
          <div>
            <p className="text-sm text-[#5C6A64] mb-3">You haven't linked a property to your account yet.</p>
            <Link to="/portal"><Button className="rounded-full bg-[#2C4C3B]">Connect your home →</Button></Link>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="font-medium text-[#1A2421]">{prop.address} · Unit {prop.unit_number}</p>
            <p className="text-sm text-[#5C6A64]">Owner on record: {prop.owner_name}</p>
            <p className="text-sm text-[#5C6A64]">Annual assessment: ${(prop.annual_assessment || 0).toFixed(2)}</p>
            <Badge className="bg-[#2C4C3B]">Linked</Badge>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function AdminTab({ profile, setProfile, save }) {
  return (
    <Card className="rounded-2xl border-[#D5CFC4]">
      <CardHeader><CardTitle className="font-serif text-2xl">Admin defaults</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-[#5C6A64]">These settings personalize outgoing communications you send from the Admin Console.</p>
        <FieldRow label="Default reply-to email" value={profile.admin_reply_to} onChange={(v) => setProfile({ ...profile, admin_reply_to: v })} testid="admin-reply-to" help="When homeowners reply to your announcements or booking decisions, replies go here." type="email" />
        <div>
          <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Email signature</label>
          <Textarea rows={4} value={profile.admin_signature || ""} onChange={(e) => setProfile({ ...profile, admin_signature: e.target.value })} className="mt-1 rounded-xl" data-testid="admin-signature" placeholder="— The HOA Board&#10;Saddleridge · Savoy Chase · The Trails" />
        </div>
        <div className="border-t border-[#D5CFC4] my-3" />
        <p className="text-xs uppercase tracking-wider text-[#5C6A64]">Board directory profile — shown publicly on /board</p>
        <FieldRow label="Board role / title" value={profile.board_title} onChange={(v) => setProfile({ ...profile, board_title: v })} testid="board-title" help="e.g. President, Treasurer, Architectural Review Chair" />
        <div>
          <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Short bio (optional)</label>
          <Textarea rows={3} value={profile.board_bio || ""} onChange={(e) => setProfile({ ...profile, board_bio: e.target.value })} className="mt-1 rounded-xl" data-testid="board-bio" placeholder="1–2 sentences about your background or focus on the board." />
        </div>
        <Button data-testid="admin-save" onClick={() => save({
          admin_reply_to: profile.admin_reply_to,
          admin_signature: profile.admin_signature,
          board_title: profile.board_title,
          board_bio: profile.board_bio,
        })} className="rounded-full bg-[#2C4C3B]">Save admin defaults</Button>
      </CardContent>
    </Card>
  );
}
